import WhereToVoteIcon from "@mui/icons-material/WhereToVote";
import AnchorIcon from "mdi-react/AnchorIcon";
import FerryIcon from "mdi-react/FerryIcon";
import LightHouseIcon from "mdi-react/LighthouseIcon";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { AtonUnit, PortListDto } from "~/api/GeneratedApi";
import { getAtonLocation } from "~/utils/getAtonLocation";
import { vesselTypeToTranslationKey } from "../functions/vesselTypeToTranslationKey";
import { VesselMapItem } from "../types/mapItems";
import { SearchListItem } from "../types/search";

interface SearchItemConverters {
  portToSearchItem: (p: PortListDto) => SearchListItem;
  vesselToSearchItem: (v: VesselMapItem) => SearchListItem;
  atonToSearchItem: (a: AtonUnit) => SearchListItem;
}

export function useSearchItemConverters(): SearchItemConverters {
  const { t } = useTranslation("vessels");

  const portToSearchItem = useCallback((port: PortListDto): SearchListItem => {
    return {
      id: `port${port.id}`,
      type: "port",
      name: port.name,
      description: port.description,
      icon: <AnchorIcon />,
    };
  }, []);

  const vesselToSearchItem = useCallback(
    (vessel: VesselMapItem): SearchListItem => {
      return {
        id: `vessel${vessel.mmsi}`,
        name: vessel.name ?? "",
        type: "vessel",
        description: vessel.callSign,
        icon: <FerryIcon />,
        tags: {
          destination: {
            value: vessel.destination ?? "",
            title: t("destination"),
            icon: <WhereToVoteIcon />,
          },
          vesselType: {
            value: t(vesselTypeToTranslationKey(vessel.shipType)),
            title: t("type"),
            icon: <FerryIcon />,
          },
        },
      };
    },
    [t],
  );

  const atonToSearchItem = useCallback(
    (aton: AtonUnit): SearchListItem => {
      const location = getAtonLocation(aton);
      return {
        id: `aton${aton.atonNumber}`,
        type: "aton",
        name: aton.name ?? aton.atonNumber?.toString(),
        description: aton.atonNumber?.toString(),
        icon: <LightHouseIcon />,
        disabled: !location,
        disabledText: t("aton:no-location"),
      };
    },
    [t],
  );

  return { portToSearchItem, vesselToSearchItem, atonToSearchItem };
}
